/**
 * Dashboard Products Insights
 *
 * Croise les données Google Ads et Merchant API pour des analyses avancées:
 * - Cartographie des catégories (ROAS vs Revenu)
 * - Saisonnalité des produits
 * - Élasticité prix/demande
 * - Top/Flop par catégorie
 * - Suggestions de bundles
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, AreaChart, Area, LabelList
} from 'recharts';
import { Loader, TrendingUp, TrendingDown, Package, Grid, BarChart2, Gift, RefreshCw, AlertCircle, Lightbulb, ShoppingCart } from 'lucide-react';
import config from '../config';
import DashboardLoadingScreen from './DashboardLoadingScreen';

const API_URL = config.API_URL;

// Fonction utilitaire pour formater les nombres
const formatNumber = (num, decimals = 0) => {
  if (num == null || isNaN(num)) return '0';
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
};

// Fonction utilitaire pour formater les montants
const formatCurrency = (amount, currency = 'EUR') => {
  if (amount == null || isNaN(amount)) return '0 €';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Couleurs pour les graphiques
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0', '#ffb366', '#66b3ff'];

// Composant Scatter avec tooltip personnalisé
const CustomScatterTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  return (
    <div style={{
      background: 'white',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
    }}>
      <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>{data.category}</p>
      <p style={{ margin: '4px 0' }}>ROAS: <strong>{data.roas.toFixed(2)}</strong></p>
      <p style={{ margin: '4px 0' }}>Revenu: <strong>{formatCurrency(data.revenue)}</strong></p>
      <p style={{ margin: '4px 0' }}>Coût: <strong>{formatCurrency(data.cost)}</strong></p>
      <p style={{ margin: '4px 0' }}>Conversions: <strong>{formatNumber(data.conversions, 1)}</strong></p>
      <p style={{ margin: '4px 0' }}>Clics: <strong>{formatNumber(data.clicks)}</strong></p>
    </div>
  );
};

// Composant pour la cartographie des catégories
const CategoryMapChart = ({ data, loading, categoryLevel, onCategoryClick }) => {
  // Hauteur fixe pour éviter les saccades
  const chartHeight = 400;

  // Conteneur vide pendant le chargement (l'overlay global s'en charge)
  if (loading || !data) {
    return <div style={{ height: chartHeight }} />;
  }

  if (data.length === 0) {
    return (
      <div style={{
        height: chartHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '12px',
        padding: '40px'
      }}>
        <div style={{ marginBottom: '12px', opacity: 0.5 }}><ShoppingCart size={48} color="#6b7280" /></div>
        <h3 style={{ margin: 0, color: '#1f2937', fontWeight: 600 }}>Aucun produit actif dans le compte</h3>
        <p style={{ color: '#6b7280', margin: 0, textAlign: 'center' }}>
          Ce compte ne contient pas de produits et n'a enregistré aucune impression sur la période sélectionnée
        </p>
      </div>
    );
  }

  // Calculer les limites pour les axes avec des paliers réguliers
  const maxRoasRaw = Math.max(...data.map(d => d.roas));
  const maxRevenueRaw = Math.max(...data.map(d => d.revenue));

  // Arrondir au palier supérieur le plus proche (pas trop éloigné)
  const roundToNiceNumber = (value) => {
    if (value <= 0) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    const normalized = value / magnitude;
    // Paliers plus fins: 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10
    if (normalized <= 1.2) return 1.2 * magnitude;
    if (normalized <= 1.5) return 1.5 * magnitude;
    if (normalized <= 2) return 2 * magnitude;
    if (normalized <= 2.5) return 2.5 * magnitude;
    if (normalized <= 3) return 3 * magnitude;
    if (normalized <= 4) return 4 * magnitude;
    if (normalized <= 5) return 5 * magnitude;
    if (normalized <= 6) return 6 * magnitude;
    if (normalized <= 8) return 8 * magnitude;
    return 10 * magnitude;
  };

  const maxRoas = roundToNiceNumber(maxRoasRaw * 1.05);
  const maxRevenue = roundToNiceNumber(maxRevenueRaw * 1.05);

  // Formatter le revenu selon l'échelle
  const formatRevenue = (v) => {
    if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M€`;
    if (v >= 1000) return `${(v / 1000).toFixed(0)}k€`;
    return `${v.toFixed(0)}€`;
  };

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          type="number"
          dataKey="roas"
          name="ROAS"
          domain={[0, maxRoas]}
          tickCount={6}
          tickFormatter={(v) => v.toFixed(1)}
          label={{ value: 'ROAS', position: 'bottom', offset: 0 }}
        />
        <YAxis
          type="number"
          dataKey="revenue"
          name="Revenu"
          domain={[0, maxRevenue]}
          tickCount={6}
          tickFormatter={formatRevenue}
          label={{ value: 'Revenu', angle: -90, position: 'insideLeft' }}
        />
        <ZAxis
          type="number"
          dataKey="conversions"
          range={[100, 1000]}
          name="Conversions"
        />
        <Tooltip content={<CustomScatterTooltip />} />
        <Scatter
          name="Catégories"
          data={data}
          fill="#8884d8"
          fillOpacity={0.7}
          cursor={categoryLevel < 5 ? 'pointer' : 'default'}
          onClick={(scatterData) => {
            if (categoryLevel < 5 && onCategoryClick && scatterData?.category) {
              onCategoryClick(scatterData.category, categoryLevel + 1);
            }
          }}
        >
          <LabelList
            dataKey="category"
            position="top"
            offset={10}
            style={{ fontSize: '11px', fill: '#333', fontWeight: 500 }}
          />
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};

// Composant pour la saisonnalité
const SeasonalityChart = ({ data, loading, category }) => {
  // Conteneur vide pendant le chargement (l'overlay global s'en charge)
  if (loading || data === null) {
    return <div style={{ height: 350 }} />;
  }

  // Cas où le chargement est terminé mais aucune donnée
  if (data.length === 0) {
    return (
      <div className="empty-state" style={{ minHeight: '350px' }}>
        <BarChart2 size={48} />
        <p>Aucune donnée de saisonnalité disponible</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={350}>
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="week"
          tick={{ fontSize: 10 }}
          interval={3}
        />
        <YAxis yAxisId="left" tickFormatter={(v) => formatNumber(v)} />
        <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v.toFixed(0)}€`} />
        <Tooltip
          formatter={(value, name) => {
            if (name === 'Revenu') return formatCurrency(value);
            if (name === 'ROAS') return value.toFixed(2);
            return formatNumber(value);
          }}
        />
        <Legend />
        <Area
          yAxisId="left"
          type="monotone"
          dataKey="conversions"
          name="Conversions"
          stroke="#8884d8"
          fill="#8884d8"
          fillOpacity={0.3}
        />
        <Area
          yAxisId="right"
          type="monotone"
          dataKey="revenue"
          name="Revenu"
          stroke="#82ca9d"
          fill="#82ca9d"
          fillOpacity={0.3}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Composant pour l'élasticité prix
const PriceElasticityTable = ({ data, loading }) => {
  // Conteneur vide pendant le chargement (l'overlay global s'en charge)
  if (loading || data === null) {
    return <div style={{ height: 350 }} />;
  }

  // Cas où le chargement est terminé mais aucune donnée
  if (data.length === 0) {
    return (
      <div className="empty-state" style={{ minHeight: '350px' }}>
        <TrendingUp size={48} />
        <p>Aucune donnée d'élasticité disponible</p>
        <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
          Les données apparaîtront après quelques jours de synchronisation des prix.
        </p>
      </div>
    );
  }

  return (
    <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
      <table className="data-table">
        <thead>
          <tr>
            <th>Produit</th>
            <th>Prix avant</th>
            <th>Prix après</th>
            <th>Var. Prix</th>
            <th>Var. Demande</th>
            <th>Élasticité</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>
          {data.slice(0, 50).map((item, index) => (
            <tr key={index}>
              <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.productId}
              </td>
              <td>{formatCurrency(item.oldPrice, item.currency)}</td>
              <td>{formatCurrency(item.newPrice, item.currency)}</td>
              <td style={{ color: item.priceDirection === 'up' ? '#ef4444' : '#10b981' }}>
                {item.priceDirection === 'up' ? '↑' : '↓'} {Math.abs(item.priceChange).toFixed(1)}%
              </td>
              <td style={{ color: item.demandChange > 0 ? '#10b981' : '#ef4444' }}>
                {item.demandChange > 0 ? '↑' : '↓'} {Math.abs(item.demandChange).toFixed(1)}%
              </td>
              <td style={{ fontWeight: 'bold' }}>
                {item.elasticity.toFixed(2)}
              </td>
              <td>
                <span className={`badge ${item.elasticityType === 'elastic' ? 'badge-warning' : 'badge-success'}`}>
                  {item.elasticityType === 'elastic' ? 'Élastique' : item.elasticityType === 'inelastic' ? 'Inélastique' : 'Unitaire'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Composant pour Top/Flop par catégorie
const TopFlopByCategory = ({ data, loading }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (data && Object.keys(data).length > 0 && !selectedCategory) {
      setSelectedCategory(Object.keys(data)[0]);
    }
  }, [data, selectedCategory]);

  // Conteneur vide pendant le chargement (l'overlay global s'en charge)
  if (loading || data === null) {
    return <div style={{ height: 350 }} />;
  }

  // Cas où le chargement est terminé mais aucune donnée
  if (Object.keys(data).length === 0) {
    return (
      <div className="empty-state" style={{ minHeight: '350px' }}>
        <Package size={48} />
        <p>Aucune donnée Top/Flop disponible</p>
      </div>
    );
  }

  const categories = Object.keys(data);
  const categoryData = selectedCategory ? data[selectedCategory] : null;

  return (
    <div>
      {/* Tabs pour les catégories */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
        {categories.slice(0, 10).map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            style={{
              padding: '6px 12px',
              borderRadius: '16px',
              border: selectedCategory === cat ? '2px solid #8884d8' : '1px solid #e0e0e0',
              background: selectedCategory === cat ? '#f0f0ff' : 'white',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {categoryData && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* Top 5 */}
          <div>
            <h4 style={{ color: '#10b981', marginBottom: '8px' }}>
              <TrendingUp size={16} style={{ marginRight: '4px' }} />
              Top 5
            </h4>
            <div className="table-container">
              <table className="data-table compact">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>ROAS</th>
                    <th>Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryData.top.map((product, i) => (
                    <tr key={i}>
                      <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {product.productTitle || product.productId}
                      </td>
                      <td style={{ color: '#10b981', fontWeight: 'bold' }}>{product.roas.toFixed(2)}</td>
                      <td>{formatNumber(product.conversions, 1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Flop 5 */}
          <div>
            <h4 style={{ color: '#ef4444', marginBottom: '8px' }}>
              <TrendingDown size={16} style={{ marginRight: '4px' }} />
              Flop 5
            </h4>
            <div className="table-container">
              <table className="data-table compact">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>ROAS</th>
                    <th>Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryData.flop.map((product, i) => (
                    <tr key={i}>
                      <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {product.productTitle || product.productId}
                      </td>
                      <td style={{ color: '#ef4444', fontWeight: 'bold' }}>{product.roas.toFixed(2)}</td>
                      <td>{formatNumber(product.conversions, 1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant pour les suggestions de bundles
const BundleSuggestions = ({ data, loading }) => {
  // Conteneur vide pendant le chargement (l'overlay global s'en charge)
  if (loading || data === null) {
    return <div style={{ height: 350 }} />;
  }

  // Cas où le chargement est terminé mais aucune donnée
  if (data.length === 0) {
    return (
      <div className="empty-state" style={{ minHeight: '350px' }}>
        <Gift size={48} />
        <p>Aucune suggestion de bundle disponible</p>
        <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
          Les suggestions apparaissent lorsque des produits sont fréquemment achetés ensemble.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
      {data.slice(0, 12).map((bundle, index) => (
        <div
          key={index}
          style={{
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
            <Gift size={20} style={{ color: '#8884d8', marginRight: '8px' }} />
            <span style={{ fontWeight: 'bold' }}>Bundle #{index + 1}</span>
            <span
              style={{
                marginLeft: 'auto',
                background: `rgba(136, 132, 216, ${0.2 + bundle.confidence * 0.6})`,
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px'
              }}
            >
              {(bundle.confidence * 100).toFixed(0)}% confiance
            </span>
          </div>
          <div style={{ fontSize: '13px' }}>
            <p style={{ margin: '4px 0', color: '#333' }}>
              <strong>1.</strong> {bundle.product1Title || bundle.product1Id}
            </p>
            <p style={{ margin: '4px 0', color: '#333' }}>
              <strong>2.</strong> {bundle.product2Title || bundle.product2Id}
            </p>
          </div>
          <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
            Achetés ensemble {bundle.coOccurrences} fois
          </div>
        </div>
      ))}
    </div>
  );
};

// Composant principal
const ProductsInsightsDashboard = ({
  token,
  sources,
  period,
  customStartDate,
  customEndDate,
  campaignIds
}) => {
  const [activeSection, setActiveSection] = useState('category-map');
  const [categoryMapData, setCategoryMapData] = useState(null);
  const [seasonalityData, setSeasonalityData] = useState(null);
  const [elasticityData, setElasticityData] = useState(null);
  const [topFlopData, setTopFlopData] = useState(null);
  const [bundlesData, setBundlesData] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  const [loadingCategoryMap, setLoadingCategoryMap] = useState(false);
  const [loadingSeasonality, setLoadingSeasonality] = useState(false);
  const [loadingElasticity, setLoadingElasticity] = useState(false);
  const [loadingTopFlop, setLoadingTopFlop] = useState(false);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [syncingPrices, setSyncingPrices] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // État pour le chargement initial et la barre de progression
  const [showContent, setShowContent] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isReady, setIsReady] = useState(false); // True quand le composant est prêt à charger
  const loadingTimeoutRef = useRef(null);
  const resetTimeoutRef = useRef(null);
  const mountTimeoutRef = useRef(null);
  const hasLoadedOnceRef = useRef(false);
  const pendingRequestsRef = useRef(new Set()); // Pour éviter les doubles requêtes
  const prevFilterKeyRef = useRef(null); // Pour détecter les changements de filtres

  const [error, setError] = useState(null);
  const [hasNoProducts, setHasNoProducts] = useState(false); // True si le compte n'a aucun produit
  const [selectedCategory, setSelectedCategory] = useState(null); // Objet { path, level, name } ou null
  const [allCategories, setAllCategories] = useState([]); // Toutes les catégories pour le dropdown saisonnalité
  const [categoryLevel, setCategoryLevel] = useState(1); // Niveau de catégorie 1 à 5
  const [categoryBreadcrumb, setCategoryBreadcrumb] = useState([]); // Fil d'Ariane des catégories

  // Fonction pour récupérer les données
  const fetchData = useCallback(async (endpoint, setter, setLoading, body = {}) => {
    console.log(`📊 [PRODUCTS-INSIGHTS] fetchData called for ${endpoint}`, { sources, period, token: !!token });

    if (!token || !sources || sources.length === 0) {
      console.log(`⚠️ [PRODUCTS-INSIGHTS] Skipping fetch: token=${!!token}, sources=`, sources);
      return;
    }

    // Éviter les doubles requêtes (React StrictMode)
    if (pendingRequestsRef.current.has(endpoint)) {
      console.log(`⚠️ [PRODUCTS-INSIGHTS] Skipping duplicate request for ${endpoint}`);
      return;
    }
    pendingRequestsRef.current.add(endpoint);

    setLoading(true);
    setError(null);

    // Simuler la progression
    setLoadingProgress(10);

    try {
      const requestBody = {
        sources,
        period,
        customStartDate,
        customEndDate,
        campaignIds,
        ...body
      };
      console.log(`📤 [PRODUCTS-INSIGHTS] Sending request to ${endpoint}:`, requestBody);

      setLoadingProgress(30);

      const response = await fetch(`${API_URL}/api/dashboard/products-insights/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      setLoadingProgress(70);

      const result = await response.json();
      console.log(`📥 [PRODUCTS-INSIGHTS] Response from ${endpoint}:`, result);

      setLoadingProgress(90);

      if (result.success) {
        setter(result.data);
        setLoadingProgress(100);

        // Détecter si le compte n'a aucun produit (category-map vide au niveau 1)
        if (endpoint === 'category-map' && categoryLevel === 1 && Array.isArray(result.data) && result.data.length === 0) {
          setHasNoProducts(true);
        }

        // Marquer le chargement initial comme terminé avec un délai pour éviter les flashs
        if (!hasLoadedOnceRef.current) {
          hasLoadedOnceRef.current = true;
          // Petit délai pour laisser React stabiliser le rendu
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
          }
          loadingTimeoutRef.current = setTimeout(() => {
            setShowContent(true);
          }, 100);
        }
      } else {
        setError(result.error || 'Erreur lors de la récupération des données');
      }
    } catch (err) {
      console.error(`❌ [PRODUCTS-INSIGHTS] Erreur ${endpoint}:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
      pendingRequestsRef.current.delete(endpoint);
    }
  }, [token, sources, period, customStartDate, customEndDate, campaignIds, categoryLevel]);

  // Fonction pour charger les catégories (appelée uniquement quand nécessaire)
  const loadAllCategories = useCallback(async () => {
    if (!token || !sources || sources.length === 0 || allCategories.length > 0 || loadingCategories) return;

    // Éviter les doubles requêtes (React StrictMode)
    if (pendingRequestsRef.current.has('all-categories')) return;
    pendingRequestsRef.current.add('all-categories');

    setLoadingCategories(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/products-insights/all-categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sources,
          period,
          customStartDate,
          customEndDate,
          campaignIds
        })
      });
      const result = await response.json();
      if (result.success && result.data) {
        setAllCategories(result.data.filter(c => c.path && c.path !== 'Non catégorisé'));
      }
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    } finally {
      setLoadingCategories(false);
      pendingRequestsRef.current.delete('all-categories');
    }
  }, [token, sources, period, customStartDate, customEndDate, campaignIds, allCategories.length, loadingCategories]);

  // Délai initial au montage pour laisser les props se stabiliser
  useEffect(() => {
    mountTimeoutRef.current = setTimeout(() => {
      setIsReady(true);
    }, 100);

    return () => {
      if (mountTimeoutRef.current) {
        clearTimeout(mountTimeoutRef.current);
      }
    };
  }, []);

  // Effet unique pour reset + chargement des données
  useEffect(() => {
    if (!sources || sources.length === 0) return;
    if (!isReady) return;

    // Créer une clé unique pour les filtres actuels
    const currentFilterKey = JSON.stringify({
      sources,
      period,
      customStartDate,
      customEndDate,
      campaignIds
    });

    // Détecter si les filtres ont changé
    const filtersChanged = prevFilterKeyRef.current !== null && prevFilterKeyRef.current !== currentFilterKey;

    // Si les filtres ont changé, faire un reset
    if (filtersChanged) {
      // Nettoyer les timeouts précédents
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }

      // Reset des données
      setCategoryMapData(null);
      setSeasonalityData(null);
      setElasticityData(null);
      setTopFlopData(null);
      setBundlesData(null);
      setAllCategories([]);
      setSelectedCategory(null);
      setCategoryLevel(1);
      setCategoryBreadcrumb([]);
      setShowContent(false);
      setLoadingProgress(0);
      setHasNoProducts(false);
      hasLoadedOnceRef.current = false;
      pendingRequestsRef.current.clear();

      // Mettre à jour la clé et sortir - le prochain render déclenchera le chargement
      prevFilterKeyRef.current = currentFilterKey;
      return;
    }

    // Mettre à jour la clé des filtres
    prevFilterKeyRef.current = currentFilterKey;

    // Charger les données de la section active
    const loadSectionData = async () => {
      switch (activeSection) {
        case 'category-map':
          if (!categoryMapData && !loadingCategoryMap) {
            const parentCategories = categoryBreadcrumb.map(item => ({ level: item.level, name: item.name }));
            await fetchData('category-map', setCategoryMapData, setLoadingCategoryMap, { categoryLevel, parentCategories });
          }
          break;
        case 'seasonality':
          // Charger les catégories si pas encore fait
          if (allCategories.length === 0 && !loadingCategories) {
            loadAllCategories();
          }
          if (!seasonalityData && !loadingSeasonality) {
            const categoryFilter = selectedCategory ? {
              categoryPath: selectedCategory.path,
              categoryLevel: selectedCategory.level,
              categoryName: selectedCategory.name
            } : {};
            await fetchData('seasonality', setSeasonalityData, setLoadingSeasonality, categoryFilter);
          }
          break;
        case 'price-elasticity':
          if (!elasticityData && !loadingElasticity) {
            await fetchData('price-elasticity', setElasticityData, setLoadingElasticity);
          }
          break;
        case 'top-flop':
          if (!topFlopData && !loadingTopFlop) {
            await fetchData('top-flop-by-category', setTopFlopData, setLoadingTopFlop);
          }
          break;
        case 'bundles':
          if (!bundlesData && !loadingBundles) {
            await fetchData('bundle-suggestions', setBundlesData, setLoadingBundles, { minCoOccurrences: 3 });
          }
          break;
        default:
          break;
      }
    };

    loadSectionData();
  }, [activeSection, sources, period, customStartDate, customEndDate, campaignIds, fetchData, categoryLevel, categoryBreadcrumb, categoryMapData, seasonalityData, elasticityData, topFlopData, bundlesData, selectedCategory, loadingCategoryMap, loadingSeasonality, loadingElasticity, loadingTopFlop, loadingBundles, allCategories.length, loadingCategories, loadAllCategories, isReady]);

  // Cleanup des timeouts au démontage
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
      if (mountTimeoutRef.current) {
        clearTimeout(mountTimeoutRef.current);
      }
    };
  }, []);

  // Recharger category map quand le niveau change (via dropdown)
  const handleCategoryLevelChange = useCallback((newLevel) => {
    setCategoryLevel(newLevel);
    setCategoryMapData(null);
    // Réinitialiser le breadcrumb si on change via dropdown
    setCategoryBreadcrumb([]);
    // Forcer le rechargement immédiat
    if (activeSection === 'category-map') {
      fetchData('category-map', setCategoryMapData, setLoadingCategoryMap, { categoryLevel: newLevel });
    }
  }, [activeSection, fetchData]);

  // Clic sur une bulle pour descendre d'un niveau
  const handleCategoryDrillDown = useCallback((categoryName, newLevel) => {
    if (newLevel <= 5) {
      // Construire le nouveau breadcrumb
      const newBreadcrumb = [...categoryBreadcrumb.slice(0, categoryLevel - 1), { level: categoryLevel, name: categoryName }];
      setCategoryBreadcrumb(newBreadcrumb);
      setCategoryLevel(newLevel);
      setCategoryMapData(null);
      // Forcer le rechargement immédiat avec les catégories parentes
      if (activeSection === 'category-map') {
        const parentCategories = newBreadcrumb.map(item => ({ level: item.level, name: item.name }));
        fetchData('category-map', setCategoryMapData, setLoadingCategoryMap, { categoryLevel: newLevel, parentCategories });
      }
    }
  }, [activeSection, fetchData, categoryLevel, categoryBreadcrumb]);

  // Clic sur le breadcrumb pour remonter à un niveau
  const handleBreadcrumbClick = useCallback((targetLevel) => {
    setCategoryLevel(targetLevel);
    setCategoryMapData(null);
    // Tronquer le breadcrumb jusqu'au niveau cliqué
    const newBreadcrumb = categoryBreadcrumb.slice(0, targetLevel - 1);
    setCategoryBreadcrumb(newBreadcrumb);
    // Forcer le rechargement immédiat avec les catégories parentes
    if (activeSection === 'category-map') {
      const parentCategories = newBreadcrumb.map(item => ({ level: item.level, name: item.name }));
      fetchData('category-map', setCategoryMapData, setLoadingCategoryMap, { categoryLevel: targetLevel, parentCategories });
    }
  }, [activeSection, fetchData, categoryBreadcrumb]);

  // Récupérer le statut de sync
  useEffect(() => {
    const fetchSyncStatus = async () => {
      if (!token) return;

      try {
        const response = await fetch(`${API_URL}/api/dashboard/products-insights/sync-status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const result = await response.json();
        if (result.success) {
          setSyncStatus(result);
        }
      } catch (err) {
        console.error('Erreur récupération statut sync:', err);
      }
    };

    fetchSyncStatus();
  }, [token]);

  // Synchroniser les prix manuellement
  const handleSyncPrices = async () => {
    if (!token) return;

    setSyncingPrices(true);
    try {
      // Pour chaque compte merchant, déclencher la sync
      for (const source of sources) {
        await fetch(`${API_URL}/api/dashboard/products-insights/sync-prices`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ accountId: source })
        });
      }

      // Rafraîchir le statut
      const response = await fetch(`${API_URL}/api/dashboard/products-insights/sync-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      if (result.success) {
        setSyncStatus(result);
      }
    } catch (err) {
      console.error('Erreur sync prix:', err);
      setError('Erreur lors de la synchronisation des prix');
    } finally {
      setSyncingPrices(false);
    }
  };

  // Sections du dashboard (certaines masquées en prod)
  const allSections = [
    { id: 'category-map', label: 'Cartographie Catégories', icon: Grid },
    { id: 'seasonality', label: 'Saisonnalité', icon: BarChart2 },
    { id: 'price-elasticity', label: 'Élasticité Prix', icon: TrendingUp, devOnly: true },
    { id: 'top-flop', label: 'Top/Flop', icon: Package, devOnly: true },
    { id: 'bundles', label: 'Bundles', icon: Gift, devOnly: true }
  ];

  // Filtrer les sections devOnly en production
  const sections = config.IS_LOCAL
    ? allSections
    : allSections.filter(s => !s.devOnly);

  if (!sources || sources.length === 0) {
    return (
      <div className="products-insights-dashboard">
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <Package size={64} style={{ color: '#ccc', marginBottom: '16px' }} />
          <h3>Sélectionnez un compte Google Ads</h3>
          <p>Les insights produits seront affichés une fois un compte sélectionné.</p>
        </div>
      </div>
    );
  }

  // Écran de chargement pleine page (même style que les autres dashboards)
  // Afficher tant que le chargement initial n'est pas terminé
  if (!showContent) {
    return (
      <div className="products-insights-dashboard">
        <DashboardLoadingScreen
          platformName="Google Ads"
          progress={loadingProgress}
          features={[
            { icon: <BarChart2 size={28} />, label: 'Cartographie catégories' },
            { icon: <TrendingUp size={28} />, label: 'Analyse saisonnalité' },
            { icon: <Lightbulb size={28} />, label: 'Insights produits' }
          ]}
        />
      </div>
    );
  }

  // Message si le compte n'a aucun produit (affichage global au niveau dashboard)
  if (hasNoProducts) {
    return (
      <div className="products-insights-dashboard">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          minHeight: '400px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ marginBottom: '24px', opacity: 0.6 }}><ShoppingCart size={64} color="#6b7280" /></div>
          <h2 style={{
            margin: '0 0 12px 0',
            fontSize: '1.5em',
            color: '#1f2937',
            fontWeight: '600'
          }}>
            Aucun produit actif dans le compte
          </h2>
          <p style={{
            color: '#6b7280',
            fontSize: '1em',
            marginBottom: '24px',
            textAlign: 'center',
            maxWidth: '500px',
            lineHeight: '1.6'
          }}>
            Ce compte Google Ads ne contient pas de produits Shopping et n'a enregistré aucune impression sur la période sélectionnée.
          </p>
          <div style={{
            background: '#f3f4f6',
            borderRadius: '8px',
            padding: '16px 24px',
            fontSize: '0.9em',
            color: '#4b5563'
          }}>
            <strong>Pour utiliser ce dashboard :</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Vérifiez que le compte possède des campagnes Shopping actives</li>
              <li>Assurez-vous que les produits sont synchronisés via Merchant Center</li>
              <li>Essayez une période plus longue si les campagnes sont récentes</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="products-insights-dashboard">
      {/* Navigation */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {sections.map(section => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '20px',
                border: activeSection === section.id ? '2px solid #8884d8' : '1px solid #e0e0e0',
                background: activeSection === section.id ? '#f0f0ff' : 'white',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: activeSection === section.id ? '600' : '400'
              }}
            >
              <Icon size={16} />
              {section.label}
            </button>
          );
        })}

        {/* Bouton de sync (masqué en prod) */}
        {config.IS_LOCAL && (
          <button
            onClick={handleSyncPrices}
            disabled={syncingPrices}
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              background: 'white',
              cursor: syncingPrices ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              color: '#666'
            }}
          >
            <RefreshCw size={14} className={syncingPrices ? 'spinning' : ''} />
            Sync prix
          </button>
        )}
      </div>

      {/* Message d'erreur */}
      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: '#dc2626',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Contenu de la section active */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          minHeight: '450px'
        }}
      >
        {activeSection === 'category-map' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Cartographie des Catégories</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: '#666' }}>Niveau :</label>
                <select
                  value={categoryLevel}
                  onChange={(e) => handleCategoryLevelChange(parseInt(e.target.value))}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <option value={1}>Niveau 1</option>
                  <option value={2}>Niveau 2</option>
                  <option value={3}>Niveau 3</option>
                  <option value={4}>Niveau 4</option>
                  <option value={5}>Niveau 5</option>
                </select>
              </div>
            </div>
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
              Positionnement des catégories selon leur ROAS (rentabilité) et leur revenu total.
              La taille des bulles représente le nombre de conversions.
              {categoryLevel < 5 && <strong> Cliquez sur une bulle pour voir le niveau suivant.</strong>}
            </p>

            {/* Fil d'Ariane */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: '10px 14px',
              background: '#f8f9fa',
              borderRadius: '8px',
              fontSize: '13px',
              flexWrap: 'wrap'
            }}>
              {/* Afficher tous les niveaux de 1 à categoryLevel */}
              {Array.from({ length: categoryLevel }, (_, i) => i + 1).map((level) => {
                const breadcrumbItem = categoryBreadcrumb.find(item => item.level === level);
                const isLast = level === categoryLevel;
                const isClickable = level < categoryLevel;

                return (
                  <React.Fragment key={level}>
                    {level > 1 && <span style={{ color: '#999' }}>›</span>}
                    <span
                      onClick={() => isClickable && handleBreadcrumbClick(level)}
                      style={{
                        cursor: isClickable ? 'pointer' : 'default',
                        color: isLast ? '#333' : '#6366f1',
                        fontWeight: isLast ? 600 : 400,
                        textDecoration: isClickable ? 'underline' : 'none'
                      }}
                    >
                      {breadcrumbItem ? breadcrumbItem.name : `Niveau ${level}`}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>

            <CategoryMapChart
              data={categoryMapData}
              loading={loadingCategoryMap}
              categoryLevel={categoryLevel}
              onCategoryClick={handleCategoryDrillDown}
            />
          </>
        )}

        {activeSection === 'seasonality' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Évolution Saisonnière</h3>
              <select
                value={selectedCategory ? selectedCategory.path : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) {
                    setSelectedCategory(null);
                  } else {
                    // Retrouver l'objet catégorie correspondant
                    const cat = allCategories.find(c => c.path === value);
                    setSelectedCategory(cat || null);
                  }
                  setSeasonalityData(null); // Reset pour recharger
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  fontSize: '13px',
                  cursor: 'pointer',
                  maxWidth: '350px'
                }}
              >
                <option value="">Toutes les catégories</option>
                {allCategories.map(cat => (
                  <option key={cat.path} value={cat.path}>
                    {'  '.repeat(cat.level - 1)}{cat.level > 1 ? '└ ' : ''}{cat.path}
                  </option>
                ))}
              </select>
            </div>
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
              Performance des produits par semaine sur la période sélectionnée.
            </p>
            <SeasonalityChart data={seasonalityData} loading={loadingSeasonality} category={selectedCategory} />
          </>
        )}

        {activeSection === 'price-elasticity' && (
          <>
            <h3 style={{ marginBottom: '16px' }}>Élasticité Prix/Demande</h3>
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
              Mesure l'impact des changements de prix sur la demande.
              Une élasticité {'>'} 1 signifie que la demande est sensible au prix.
            </p>
            <PriceElasticityTable data={elasticityData} loading={loadingElasticity} />
          </>
        )}

        {activeSection === 'top-flop' && (
          <>
            <h3 style={{ marginBottom: '16px' }}>Top/Flop par Catégorie</h3>
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
              Meilleurs et moins bons produits par catégorie, basé sur le ROAS.
            </p>
            <TopFlopByCategory data={topFlopData} loading={loadingTopFlop} />
          </>
        )}

        {activeSection === 'bundles' && (
          <>
            <h3 style={{ marginBottom: '16px' }}>Suggestions de Bundles</h3>
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
              Produits fréquemment achetés ensemble, identifiés par l'analyse des conversions.
            </p>
            <BundleSuggestions data={bundlesData} loading={loadingBundles} />
          </>
        )}
      </div>

      <style>{`
        .products-insights-dashboard .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          color: #666;
          min-height: 300px;
        }

        .products-insights-dashboard .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .products-insights-dashboard .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .products-insights-dashboard .data-table th,
        .products-insights-dashboard .data-table td {
          padding: 8px 12px;
          text-align: left;
          border-bottom: 1px solid #e0e0e0;
        }

        .products-insights-dashboard .data-table th {
          background: #f8f9fa;
          font-weight: 600;
          position: sticky;
          top: 0;
        }

        .products-insights-dashboard .data-table.compact th,
        .products-insights-dashboard .data-table.compact td {
          padding: 6px 8px;
          font-size: 12px;
        }

        .products-insights-dashboard .badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }

        .products-insights-dashboard .badge-success {
          background: #dcfce7;
          color: #166534;
        }

        .products-insights-dashboard .badge-warning {
          background: #fef3c7;
          color: #92400e;
        }

        .products-insights-dashboard .table-container {
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ProductsInsightsDashboard;
